import type { FunctionComponent } from 'react';
import { useMemo, useCallback, useState } from 'react';
import { FormattedDate, FormattedMessage } from 'react-intl';

import { useAutoAnimate } from '@formkit/auto-animate/react';

import { Button, ButtonSize, Rounding } from '../../../shared/components/button/button';
import { ContentMarkdown } from '../../../shared/components/content-markdown/content-markdown';
import { Gap } from '../../../shared/components/gap/gap';
import { createClassName, createStyleHelper } from '../../../shared/utils/class-names';
import { getAssetUrl } from '../../../shared/utils/get-asset-url';
import { slugify } from '../../../shared/utils/strings';
import { IConsumeTypeStrategy } from '../../graphql/available-bonuses.graphql';
import type { IBonus } from '../bonuses/bonuses';

import styles from './available-bonus-card.module.scss';

const style = createStyleHelper(styles, 'available-bonus-card');

export interface IAvailableBonusCardProps {
    className?: string;
    id: string;
    image?: string | null;
    title: string;
    shortDescription: string;
    longDescription: string;
    bonusExpiryDate?: string | null;
    consumeTypeStrategy?: IConsumeTypeStrategy | null;
    isClaimable: boolean;
    isClaiming: boolean;
    bonus: IBonus;
    onClaim: (id: string) => void;
}

export const AvailableBonusCard: FunctionComponent<IAvailableBonusCardProps> = ({
    className,
    id,
    image,
    title,
    shortDescription,
    longDescription,
    bonusExpiryDate,
    isClaimable,
    isClaiming,
    consumeTypeStrategy,
    bonus,
    onClaim,
}) => {
    const [expandArea, setExpandArea] = useState(false);

    const toggleView = useCallback(() => {
        setExpandArea((state) => !state);
    }, []);

    const claimBonus = useCallback(() => {
        onClaim(id);
    }, [onClaim, id]);
    const [ref] = useAutoAnimate<HTMLDivElement>();

    const contentVariables = useMemo(
        () => ({
            wageringRequirementAmount: bonus.wageringRequirementAmount ?? 'error',
            wageredAmount: bonus.wageredAmount ?? 0,
        }),
        [bonus],
    );
    const imageUrl = getAssetUrl(image);

    return (
        <div className={createClassName([className, style()])}>
            {imageUrl ? <img className={style('image')} src={imageUrl} /> : null}
            <Gap className={style('content')}>
                <h3 className={style('title')}>{title}</h3>
                <p className={style('description')}>{shortDescription}</p>

                <button onClick={toggleView} className={style('expand-button')}>
                    {expandArea ? (
                        <FormattedMessage
                            id="account.bonuses.less-info"
                            defaultMessage="Less Info"
                        />
                    ) : (
                        <FormattedMessage
                            id="account.bonuses.more-info"
                            defaultMessage="More Info"
                        />
                    )}
                </button>
            </Gap>
            <Gap direction="horizontal" className={style('expiry')}>
                <span className={style('expiration-label')}>
                    <FormattedMessage
                        id="account.bonuses.available-bonus.expiry"
                        defaultMessage={'Expires'}
                    />
                </span>
                {bonusExpiryDate ? (
                    <FormattedDate value={bonusExpiryDate} />
                ) : (
                    <FormattedMessage
                        id="account.bonuses.available-bonus.no-expiration"
                        defaultMessage={'No Expiration'}
                    />
                )}
            </Gap>
            {/** Superficial div is required for autoAnimate on grid */}
            <div className={style('more-info')}>
                <Gap ref={ref}>
                    {expandArea ? (
                        <ContentMarkdown values={contentVariables} markdown={longDescription} />
                    ) : null}

                    {consumeTypeStrategy ? (
                        <p className={style('consume-type')}>
                            <FormattedMessage
                                id={`account.bonuses.consume-type.${slugify(consumeTypeStrategy)}`}
                                defaultMessage={
                                    consumeTypeStrategy === IConsumeTypeStrategy.BonusMoneyFirst
                                        ? 'Bonus money will be consumed first'
                                        : 'Your real money will be consumed first'
                                }
                            />
                        </p>
                    ) : null}
                </Gap>
            </div>

            <div className={style('actions')}>
                <Button
                    size={ButtonSize.MEDIUM}
                    hollow
                    rounding={Rounding.NONE}
                    disabled={!isClaimable || isClaiming}
                    onClick={claimBonus}
                >
                    <FormattedMessage
                        id="account.bonuses.claim-bonus"
                        defaultMessage="Claim Bonus"
                    />
                </Button>
            </div>
        </div>
    );
};
